import { router } from "@inertiajs/react"
import { Loader2, RefreshCw } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import type {
  IssueSeverity,
  QualityFilters,
  QualityScanStatus,
  QualitySummary,
  QualityUrls,
} from "@/lib/types"

interface Props {
  summary: QualitySummary
  filters: QualityFilters
  scanStatus: QualityScanStatus
  urls: QualityUrls
}

function relativeTime(iso: string | null): string {
  if (!iso) return "never"
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return "unknown"
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (diffSec < 60) return "just now"
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`
  if (diffSec < 86_400) return `${Math.floor(diffSec / 3600)}h ago`
  return `${Math.floor(diffSec / 86_400)}d ago`
}

const SEVERITY_BUCKETS: Array<{ key: IssueSeverity; label: string }> = [
  { key: "critical", label: "Critical" },
  { key: "high", label: "High" },
  { key: "medium", label: "Medium" },
]

// Toggle the severity filter via Inertia visit. Re-clicking the active filter
// clears it. Other filters (issue_type) are preserved.
function applyFilter(
  urls: QualityUrls,
  currentFilters: QualityFilters,
  severity: IssueSeverity,
) {
  const next: Record<string, string> = {}
  if (currentFilters.issue_type) next.issue_type = currentFilters.issue_type
  if (currentFilters.severity !== severity) next.severity = severity
  router.visit(urls.quality, { data: next, preserveScroll: true })
}

function runScan(urls: QualityUrls) {
  router.post(urls.run_scan, {}, { preserveScroll: true })
}

export function QualitySummaryBar({ summary, filters, scanStatus, urls }: Props) {
  // Treat queued (backfill enqueued, scan job not yet started) as in-flight —
  // hides the Run-scan-now button so a curator can't double-queue, and the
  // page's polling effect keeps refreshing until the status reaches completed.
  const isInFlight = scanStatus.status === "running" || scanStatus.status === "queued"
  const inFlightLabel = scanStatus.status === "queued" ? "Queued…" : "Scanning…"
  const lastLabel = relativeTime(scanStatus.last_completed_at)

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center justify-between gap-3 py-3">
        {/* Severity-bucketed counts as clickable filter chips */}
        <div
          className="flex flex-wrap items-center gap-2"
          role="toolbar"
          aria-label="Filter by severity"
        >
          {SEVERITY_BUCKETS.map((bucket) => {
            const count = summary[bucket.key]
            const active = filters.severity === bucket.key
            return (
              <button
                key={bucket.key}
                type="button"
                onClick={() => applyFilter(urls, filters, bucket.key)}
                aria-pressed={active}
                aria-label={`${bucket.label}: ${count} issues`}
                className="rounded-full transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Badge
                  variant={active ? "default" : "outline"}
                  className="cursor-pointer"
                  data-severity={bucket.key}
                >
                  <span>{bucket.label}</span>
                  <span className="ml-1 tabular-nums">({count})</span>
                </Badge>
              </button>
            )
          })}
        </div>

        {/* Last-scan + Run scan now */}
        <div className="flex items-center gap-3">
          <span
            className={
              "text-xs tabular-nums " +
              (scanStatus.stale
                ? "text-amber-600 dark:text-amber-400"
                : "text-muted-foreground")
            }
            aria-label={
              scanStatus.stale
                ? `Last scan stale, ${lastLabel}`
                : `Last scan ${lastLabel}`
            }
          >
            Last scan: {lastLabel}
            {scanStatus.stale && scanStatus.last_completed_at ? " (stale)" : ""}
          </span>

          {isInFlight ? (
            <Badge variant="secondary" className="gap-1.5">
              <Loader2 className="size-3 animate-spin" aria-hidden />
              <span>{inFlightLabel}</span>
            </Badge>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => runScan(urls)}
              aria-label="Run quality scan now"
            >
              <RefreshCw className="mr-1.5 size-4" aria-hidden />
              Run scan now
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
